import { faX } from "@fortawesome/pro-light-svg-icons";
import {
    faChevronDown,
    faChevronRight,
    faChevronUp,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faCopy } from "@fortawesome/sharp-light-svg-icons";
import { Disclosure } from "@headlessui/react";
import { useQuery } from "@tanstack/react-query";
import { createColumnHelper } from "@tanstack/react-table";
import { useCopyToClipboard } from "@uidotdev/usehooks";
import { useAtom } from "jotai";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import type {
    DataPointHistory,
    EmissionRecordView,
} from "../../../../apiClient/generated";
import {
    useEmissionEventsApiClient,
    useEmissionRecordsApiClient,
} from "../../../../hooks";
import { LinkButton } from "../../../../ui/Buttons";
import { CoordinatesField } from "../../../../ui/GenericFields";
import { formatDate, formatDateTime } from "../../../../utils/dates";
import { CompactTableUI, useDataTableV3 } from "../../../DataTable";
import {
    currentTrackedData,
    DataPointHistoryDataList,
} from "../../../Emissions/DataPointHistoryData";
import { EmissionPdfDownloadButton } from "../../../Emissions/EmissionPdfDownloadButton";
import { EditableUserFieldDisplay } from "../../../Emissions/EmissionRecordDisplay";
import { RateWithUncertaintyCell } from "../../../Emissions/Extras";
import { EventFiles } from "../../../EventsV2/EventFiles";
import { useCompanyDataForEmission } from "../../../EventsV2/helpers";
import { selectionContextAtom } from "../state";
import { DAILY_AVERAGE_PROVIDER_NAMES } from "./Timeline/Satellite/marks";

const DataRow = ({
    label,
    children,
}: {
    label: string;
    children: string | ReactNode;
}) => {
    const [, copyToClipboard] = useCopyToClipboard();

    return (
        <div className="flex items-center justify-between w-full text-sm whitespace-nowrap">
            <div className="text-neutral-500">{label}</div>
            <div className="flex items-center justify-end truncate w-2/3 pr-0.5">
                {typeof children === "string" && children.length > 45 ? (
                    <span title={children}>
                        {children.substring(0, 30)}...
                        <button
                            type="button"
                            className="ml-1 rounded hover:bg-gray-100"
                            onClick={() => copyToClipboard(children)}
                        >
                            <FontAwesomeIcon icon={faCopy} />
                        </button>
                    </span>
                ) : (
                    children
                )}
            </div>
        </div>
    );
};

type ExtendedDataPointHistory = DataPointHistory & {
    current?: boolean;
    changedAt?: Date;
};

const historyColumnHelper = createColumnHelper<ExtendedDataPointHistory>();

const rateWithUncertainty = (
    rate?: number | null,
    uncertainty?: number | null,
    units?: string,
    convertFn: (val: number) => number = (val) => val,
) => {
    if (rate == null) {
        return "-";
    }
    return (
        <>
            {convertFn(rate).toFixed(2)}{" "}
            {uncertainty != null && (
                <span>± {convertFn(uncertainty).toFixed(2)} </span>
            )}
            {units}
        </>
    );
};

const historyColumns = [
    historyColumnHelper.accessor("version", {
        id: "version",
        header: "Version",
        cell: (info) => info.getValue() || "-",
        enableColumnFilter: false,
        enableSorting: false,
    }),
    historyColumnHelper.accessor(
        (row) => [row.changedAt, row.createdAt, row.current],
        {
            id: "changedAt",
            header: "Modified",
            cell: (info) => {
                const [changedAt, createdAt, current] = info.getValue();
                const value = changedAt
                    ? (changedAt as Date)
                    : (createdAt as Date);
                if (!changedAt && current) {
                    return "-";
                }
                return formatDateTime(value);
            },
            enableColumnFilter: false,
            enableSorting: false,
        },
    ),
    historyColumnHelper.accessor(
        (row) => [row.detectedRate, row.detectedRateUncertainty],
        {
            id: "detectedRate",
            header: "Detected Rate",
            cell: (info) => {
                const [detectedRate, detectedRateUncertainty] = info.getValue();
                return rateWithUncertainty(
                    detectedRate,
                    detectedRateUncertainty,
                    "kg/h",
                    (val: number) => val / 1000,
                );
            },
            enableColumnFilter: false,
            enableSorting: false,
        },
    ),
    historyColumnHelper.accessor(
        (row) => [row.concentration, row.concentrationUncertainty],
        {
            id: "concentration",
            header: "Concentration",
            cell: (info) => {
                const [concentration, concentrationUncertainty] =
                    info.getValue();
                return rateWithUncertainty(
                    concentration,
                    concentrationUncertainty,
                    "ppm*m",
                );
            },
            enableColumnFilter: false,
            enableSorting: false,
        },
    ),
    historyColumnHelper.accessor("location", {
        id: "location",
        header: "Location",
        cell: (info) => {
            const value = info.getValue();
            if (value) {
                return (
                    <CoordinatesField
                        coordinates={value.coordinates}
                        allowCopy
                    />
                );
            }
            return "-";
        },
        size: 160,
        enableSorting: false,
    }),
    historyColumnHelper.accessor("data", {
        id: "data",
        header: "Details",
        cell: (info) => <DataPointHistoryDataList data={info.getValue()} />,
        enableColumnFilter: false,
        enableSorting: false,
    }),
];

interface DataPointHistoryTableProps {
    dataPoint: EmissionRecordView["dataPoint"];
}

const DataPointHistoryTable = ({ dataPoint }: DataPointHistoryTableProps) => {
    const fetchFunction = async () => {
        const data: ExtendedDataPointHistory[] = [
            {
                id: dataPoint.id,
                version: dataPoint.version,
                changedAt: undefined,
                current: true,
                detectionLimit: dataPoint.detectionLimit,
                detectedRate: dataPoint.detectedRate,
                detectedRateUncertainty: dataPoint.detectedRateUncertainty,
                concentration: dataPoint.concentration,
                concentrationUncertainty: dataPoint.concentrationUncertainty,
                location: dataPoint.location,
                createdAt: dataPoint.createdAt,
                data: currentTrackedData(dataPoint.data, dataPoint.history),
            },
            ...dataPoint.history,
        ];
        return { results: data, count: data.length };
    };

    const { table, dataQuery, dataName } =
        useDataTableV3<ExtendedDataPointHistory>({
            dataName: `datapoint-history-${dataPoint.id}`,
            columns: historyColumns,
            extraFilters: {},
            fetchFunction,
            sortable: false,
            filterable: false,
            defaultPageSize: 500,
        });

    return (
        <CompactTableUI<ExtendedDataPointHistory>
            table={table}
            dataName={dataName}
            columns={historyColumns}
            loadingData={dataQuery.isFetching}
            itemCount={dataQuery.data?.count || 0}
            showPagination={false}
        />
    );
};

interface MultipleEmissionsDisplayProps {
    emissionRecords?: EmissionRecordView[];
}

const BRIDGER_FIELDS: Array<[string, string]> = [
    ["bridger_equipment_id", "Equipment ID"],
    ["bridger_equipment_type", "Equipment Type"],
    ["bridger_emission_location_id", "Emission Location ID"],
    ["bridger_detection_id", "Detection ID"],
    ["bridger_persistence", "Persistence"],
    ["emitter_height_(ft)", "Emitter Height (ft)"],
];

export const MultipleEmissionsDisplay = (
    props: MultipleEmissionsDisplayProps,
) => {
    const [expandData, setExpandData] = useState(false);
    const [currentEmission, setCurrentEmission] = useState(0);
    const [, setSelectionContext] = useAtom(selectionContextAtom);

    // The records list can shrink between renders (a refetch with fewer linked
    // events, or this component being reused for a different emission), which
    // leaves the paging index pointing past the end.
    const currentIndex = Math.min(
        currentEmission,
        (props.emissionRecords?.length ?? 0) - 1,
    );

    // Publish the selected record so the map follows the arrows. One-way.
    const focused = props.emissionRecords?.[currentIndex];
    useEffect(() => {
        setSelectionContext((s) =>
            s.focusedEmissionId === focused?.id
                ? s
                : {
                      ...s,
                      focusedEmissionId: focused?.id,
                      focusedPlumeId: focused?.dataPoint?.plumeImage,
                  },
        );
    }, [focused, setSelectionContext]);

    // Blank component if no data is passed.
    if (!props.emissionRecords || props.emissionRecords.length === 0) {
        return null;
    }

    const selectedEmission = props.emissionRecords[currentIndex];
    const bridgerRows = BRIDGER_FIELDS.flatMap(([key, label]) => {
        const raw = selectedEmission.dataPoint?.data?.[key];
        return raw != null && raw !== ""
            ? [{ key, label, value: String(raw) }]
            : [];
    });
    const extraData = [
        {
            label: "Import datetime",
            value: formatDateTime(selectedEmission.dataPoint?.createdAt),
        },
        {
            label: "Match datetime",
            value: formatDateTime(selectedEmission.createdAt),
        },
        ...Object.keys(selectedEmission.dataPoint?.data || []).map(
            (key: string) => {
                return {
                    label: key.replaceAll("_", " "),
                    value: selectedEmission.dataPoint?.data[key] as string,
                };
            },
        ),
    ];

    return (
        <>
            <div className="flex justify-between items-center mt-1">
                <div className="flex items-center gap-2">
                    <h2 className="text-sm font-semibold text-neutral-800 tracking-tight">
                        Emission Data
                        {props.emissionRecords.length > 1 && (
                            <span className="text-neutral-400 font-normal ml-1">
                                ({props.emissionRecords.length} linked events)
                            </span>
                        )}
                    </h2>
                    {props.emissionRecords.length > 1 && currentIndex === 0 && (
                        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                            Main event
                        </span>
                    )}
                </div>

                {props.emissionRecords.length > 1 && (
                    <div className="flex items-center gap-1 bg-neutral-100 rounded-lg p-1">
                        <button
                            type="button"
                            className="w-4 h-4 flex items-center justify-center rounded-md text-neutral-500 hover:bg-white hover:text-neutral-800 hover:shadow-sm transition-all duration-150"
                            onClick={(e) => {
                                e.preventDefault();
                                setCurrentEmission(
                                    (currentIndex -
                                        1 +
                                        props.emissionRecords.length) %
                                        props.emissionRecords.length,
                                );
                            }}
                        >
                            <FontAwesomeIcon
                                icon={faChevronLeft}
                                className="w-3 h-3"
                            />
                        </button>
                        <span className="text-xs font-medium text-neutral-600 tabular-nums px-2 min-w-[3rem] text-center">
                            {currentIndex + 1}
                            <span className="text-neutral-400 mx-0.5">/</span>
                            {props.emissionRecords.length}
                        </span>
                        <button
                            type="button"
                            className="w-4 h-4 flex items-center justify-center rounded-md text-neutral-500 hover:bg-white hover:text-neutral-800 hover:shadow-sm transition-all duration-150"
                            onClick={(e) => {
                                e.preventDefault();
                                setCurrentEmission(
                                    (currentIndex + 1) %
                                        props.emissionRecords.length,
                                );
                            }}
                        >
                            <FontAwesomeIcon
                                icon={faChevronRight}
                                className="w-3 h-3"
                            />
                        </button>
                    </div>
                )}
            </div>

            {selectedEmission.dataPoint.secondaryDataSource?.includes(
                "Sentinel-5P",
            ) && (
                <Disclosure
                    as="div"
                    className="rounded border border-yellow-300 bg-yellow-100 px-2 py-1 text-sm mb-1"
                    defaultOpen
                >
                    {({ open }) => (
                        <>
                            <Disclosure.Button className="flex justify-between w-full items-center gap-1 font-bold text-yellow-700">
                                NOTE: Sentinel-5/TROPOMI
                                <FontAwesomeIcon
                                    icon={open ? faChevronUp : faChevronDown}
                                />
                            </Disclosure.Button>
                            <Disclosure.Panel>
                                <p className="w-full text-yellow-900 mb-1">
                                    Sentinel-5/TROPOMI detections are excluded
                                    from AERSHED analytics due to the likelihood
                                    the plumes are a collection of emissions
                                    from multiple facilities and/or industrial
                                    sources.
                                </p>
                                <p className="w-full text-yellow-900">
                                    The data is provided for informational
                                    purposes only as the published latitude and
                                    longitude is near your facility boundary.
                                </p>
                            </Disclosure.Panel>
                        </>
                    )}
                </Disclosure>
            )}

            {DAILY_AVERAGE_PROVIDER_NAMES.has(selectedEmission.providerName) ? (
                <>
                    <Disclosure
                        as="div"
                        className="rounded border border-yellow-300 bg-yellow-100 px-2 py-1 text-sm mb-1"
                        defaultOpen
                    >
                        {({ open }) => (
                            <>
                                <Disclosure.Button className="flex justify-between w-full items-center gap-1 font-bold text-yellow-700">
                                    NOTE: 24-hour average
                                    <FontAwesomeIcon
                                        icon={
                                            open ? faChevronUp : faChevronDown
                                        }
                                    />
                                </Disclosure.Button>
                                <Disclosure.Panel>
                                    <p className="w-full text-yellow-900">
                                        This event is a daily average of the
                                        site emission rate, not a single
                                        detection.
                                    </p>
                                </Disclosure.Panel>
                            </>
                        )}
                    </Disclosure>
                    <DataRow label="Averaging window">
                        {formatDateTime(
                            selectedEmission.dataPoint.detectionTimestamp,
                        )}
                        {" – "}
                        {formatDateTime(
                            new Date(
                                selectedEmission.dataPoint.detectionTimestamp.getTime() +
                                    24 * 60 * 60 * 1000,
                            ),
                        )}
                    </DataRow>
                </>
            ) : (
                <DataRow label="Detection date">
                    {formatDateTime(
                        selectedEmission.dataPoint.detectionTimestamp,
                    )}
                </DataRow>
            )}

            {selectedEmission.dataPoint.detectedRate && (
                <DataRow label="Detected rate">
                    <RateWithUncertaintyCell
                        unit="kg/h"
                        rate={
                            selectedEmission.dataPoint.detectedRate
                                ? selectedEmission.dataPoint.detectedRate / 1000
                                : undefined
                        }
                        uncertainty={
                            selectedEmission.dataPoint.detectedRateUncertainty
                                ? selectedEmission.dataPoint
                                      .detectedRateUncertainty / 1000
                                : undefined
                        }
                    />
                </DataRow>
            )}
            {selectedEmission.dataPoint.concentration && (
                <DataRow label="Concentration">
                    <RateWithUncertaintyCell
                        unit="ppm*m"
                        rate={selectedEmission.dataPoint.concentration}
                        uncertainty={
                            selectedEmission.dataPoint.concentrationUncertainty
                        }
                    />
                </DataRow>
            )}
            <DataRow label="Provider name">
                {selectedEmission.dataPoint.providerName}
            </DataRow>
            <DataRow label="Secondary data source">
                {selectedEmission.dataPoint.secondaryDataSource || "-"}
            </DataRow>
            <DataRow label="Coordinates">
                <CoordinatesField
                    coordinates={
                        selectedEmission.dataPoint.location.coordinates
                    }
                    allowCopy
                    mapLinkParams={{
                        emissionId: selectedEmission.id,
                        lat: selectedEmission.dataPoint.location.coordinates[1].toString(),
                        lon: selectedEmission.dataPoint.location.coordinates[0].toString(),
                    }}
                />
            </DataRow>
            {bridgerRows.length > 0 && (
                <>
                    <div className="text-sm font-semibold text-neutral-800 tracking-tight mt-2">
                        Bridger Data
                    </div>
                    {bridgerRows.map((row) => (
                        <DataRow key={row.key} label={row.label}>
                            {row.value}
                        </DataRow>
                    ))}
                </>
            )}
            {extraData.length > 0 && (
                <DataRow label="">
                    <LinkButton onClick={() => setExpandData((i) => !i)}>
                        {expandData
                            ? "Hide extra emission data"
                            : "Show all emission data"}
                        <FontAwesomeIcon
                            icon={expandData ? faChevronUp : faChevronDown}
                        />
                    </LinkButton>
                </DataRow>
            )}
            {expandData &&
                extraData.map((item) => (
                    <DataRow key={item.value} label={item.label}>
                        {item.value}
                    </DataRow>
                ))}
        </>
    );
};

interface EmissionDetailProps {
    emissionId: string;
    expandData: (emissionId: string) => void;
    onChangeEmission: (emissionId?: string) => void;
}

export const EmissionDetail = (props: EmissionDetailProps) => {
    const apiClient = useEmissionRecordsApiClient();
    const eventsApiClient = useEmissionEventsApiClient();
    const [, copyEmissionId] = useCopyToClipboard();

    // Query site data
    const emissionQuery = useQuery({
        queryKey: ["emissionRecord", props.emissionId],
        queryFn: async () => {
            const response = await apiClient.emissionRecordsRetrieve({
                id: props.emissionId,
            });
            return response;
        },
        refetchOnWindowFocus: false,
    });
    const { company } = useCompanyDataForEmission({
        emissionRecord: emissionQuery.data,
    });

    // Linked emission info (when this event linked to another event)
    const mainLinkEmissionQuery = useQuery({
        queryKey: ["event", emissionQuery.data?.mainEvent.mainEvent],
        queryFn: async () => {
            const response = await eventsApiClient.emissioneventsRetrieve({
                id: emissionQuery.data?.mainEvent.mainEvent,
            });
            return response.mainEmissionRecord;
        },
        refetchOnWindowFocus: false,
        enabled: !!emissionQuery.data?.mainEvent.mainEvent,
    });

    // When other events are linked here
    const allRelatedEmissionsQuery = useQuery({
        queryKey: ["emissionsRelatedToEvent", emissionQuery.data?.mainEvent],
        queryFn: async () => {
            const relatedEvent = emissionQuery.data?.mainEvent.id;
            if (!relatedEvent) {
                return [];
            }
            const response = await apiClient.emissionRecordsList({
                relatedEvent,
                pageSize: 500,
            });
            return response.results;
        },
        enabled:
            !!emissionQuery.data && !emissionQuery.data.mainEvent.mainEvent,
        refetchOnWindowFocus: false,
        refetchOnMount: true,
    });

    return (
        <div className="flex-1 overflow-scroll py-1 pl-2 pr-4 text-sm w-full flex flex-col gap-0.5">
            <div className="w-full flex items-center justify-between mb-2">
                <h1 className="font-bold">
                    Overview:{" "}
                    {emissionQuery.data?.dataPoint.detectedRate != null
                        ? (
                              emissionQuery.data.dataPoint.detectedRate / 1000
                          ).toFixed()
                        : "-"}{" "}
                    kg/h: {emissionQuery.data?.providerName} (
                    {formatDate(
                        emissionQuery.data?.dataPoint.detectionTimestamp,
                    )}
                    )
                </h1>
                <div className="flex items-center gap-2">
                    {/* Remount (resetting any stale/in-flight download state)
                        whenever the event is saved — updatedAt bumps server-side
                        on every edit, so a plain refetch won't remount. */}
                    <EmissionPdfDownloadButton
                        key={emissionQuery.data?.mainEvent.updatedAt?.toISOString()}
                        emissionId={props.emissionId}
                    />
                    <button
                        type="button"
                        className="inline-flex items-center gap-1.5 shrink-0 whitespace-nowrap rounded px-2 py-0.5 text-xs text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                        onClick={() => props.onChangeEmission()}
                    >
                        <FontAwesomeIcon icon={faX} className="size-3" />
                        Close
                    </button>
                </div>
            </div>

            <DataRow label="Emission ID">
                <span className="flex items-center gap-1.5">
                    <span className="font-mono text-xs select-all">
                        {props.emissionId}
                    </span>
                    <button
                        type="button"
                        className="rounded px-1 py-0.5 text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100"
                        onClick={() => copyEmissionId(props.emissionId)}
                        title="Copy emission ID"
                    >
                        <FontAwesomeIcon icon={faCopy} className="size-3" />
                    </button>
                </span>
            </DataRow>
            <DataRow label="Status">
                <span className="capitalize font-bold">
                    {emissionQuery.data?.mainEvent.eventStatus
                        .replaceAll("_", " ")
                        .toLowerCase()}
                </span>
            </DataRow>
            <DataRow label="Assignee">
                <EditableUserFieldDisplay
                    emissionData={emissionQuery.data}
                    field="responsible"
                />
            </DataRow>
            {company?.settings.approvalFlowEnabled && (
                <DataRow label="Coordinator">
                    <EditableUserFieldDisplay
                        emissionData={emissionQuery.data}
                        field="reviewer"
                    />
                </DataRow>
            )}
            <DataRow label="Root cause">
                {emissionQuery.data?.mainEvent.formData?.ae_root_cause || "-"}
            </DataRow>
            {mainLinkEmissionQuery.data && (
                <DataRow label="">
                    <LinkButton
                        onClick={() =>
                            props.onChangeEmission(mainLinkEmissionQuery.data)
                        }
                    >
                        Click to view main event
                    </LinkButton>
                </DataRow>
            )}
            {!emissionQuery.data?.mainEvent.mainEvent && (
                <DataRow label="">
                    <LinkButton
                        onClick={() => props.expandData(props.emissionId)}
                    >
                        Open reporting form
                        <FontAwesomeIcon icon={faChevronRight} />
                    </LinkButton>
                </DataRow>
            )}

            <MultipleEmissionsDisplay
                key={props.emissionId}
                emissionRecords={useMemo(() => {
                    let emissions: EmissionRecordView[] = [];
                    if (emissionQuery.data) {
                        emissions.push(emissionQuery.data);
                    }
                    if (allRelatedEmissionsQuery.data) {
                        emissions = emissions.concat(
                            allRelatedEmissionsQuery.data,
                        );
                    }
                    return emissions;
                }, [emissionQuery.data, allRelatedEmissionsQuery.data])}
            />

            {emissionQuery.data?.dataPoint.history.length > 0 && (
                <Disclosure as="div" className="mt-1" defaultOpen>
                    {({ open }) => (
                        <>
                            <Disclosure.Button className="flex rounded hover:bg-neutral-50 justify-between w-full items-center gap-1 text-sm font-semibold text-neutral-800 tracking-tight">
                                Data Point History
                                <FontAwesomeIcon
                                    icon={open ? faChevronUp : faChevronDown}
                                    className="w-3 h-3 text-neutral-500"
                                />
                            </Disclosure.Button>
                            <Disclosure.Panel className="mt-1">
                                <DataPointHistoryTable
                                    dataPoint={emissionQuery.data?.dataPoint}
                                />
                            </Disclosure.Panel>
                        </>
                    )}
                </Disclosure>
            )}

            {(emissionQuery.data?.mainEvent.formData?.ae_notes ||
                emissionQuery.data?.mainEvent.extraNotes !== "") && (
                <Disclosure as="div" className="mt-1">
                    {({ open }) => (
                        <>
                            <Disclosure.Button className="flex rounded hover:bg-neutral-50 justify-between w-full items-center gap-1 text-sm font-semibold text-neutral-800 tracking-tight">
                                Notes
                                <FontAwesomeIcon
                                    icon={open ? faChevronUp : faChevronDown}
                                    className="w-3 h-3 text-neutral-500"
                                />
                            </Disclosure.Button>
                            <Disclosure.Panel className="mt-1">
                                {emissionQuery.data?.mainEvent.formData
                                    ?.notes !== "" && (
                                    <p>
                                        {
                                            emissionQuery.data?.mainEvent
                                                .formData.ae_notes
                                        }
                                    </p>
                                )}
                                {emissionQuery.data?.mainEvent.extraNotes !==
                                    "" && (
                                    <p>
                                        {
                                            emissionQuery.data?.mainEvent
                                                .extraNotes
                                        }
                                    </p>
                                )}
                            </Disclosure.Panel>
                        </>
                    )}
                </Disclosure>
            )}

            {emissionQuery.data?.mainEvent.eventStatus === "REJECTED" &&
                emissionQuery.data?.mainEvent.formData?.ae_reject_reason !==
                    "" && (
                    <Disclosure as="div" className="mt-1">
                        {({ open }) => (
                            <>
                                <Disclosure.Button className="flex rounded hover:bg-neutral-50 justify-between w-full items-center gap-1 text-sm font-semibold text-neutral-800 tracking-tight">
                                    Rejection Reason
                                    <FontAwesomeIcon
                                        icon={
                                            open ? faChevronUp : faChevronDown
                                        }
                                        className="w-3 h-3 text-neutral-500"
                                    />
                                </Disclosure.Button>
                                <Disclosure.Panel className="mt-1">
                                    {
                                        emissionQuery.data?.mainEvent.formData
                                            ?.ae_reject_reason
                                    }
                                </Disclosure.Panel>
                            </>
                        )}
                    </Disclosure>
                )}

            {emissionQuery.data && (
                <Disclosure as="div" className="mt-1">
                    {({ open }) => (
                        <>
                            <Disclosure.Button className="flex rounded hover:bg-neutral-50 justify-between w-full items-center gap-1 text-sm font-semibold text-neutral-800 tracking-tight">
                                Attachments
                                <FontAwesomeIcon
                                    icon={open ? faChevronUp : faChevronDown}
                                    className="w-3 h-3 text-neutral-500"
                                />
                            </Disclosure.Button>
                            <Disclosure.Panel className="mt-1">
                                <EventFiles
                                    event={emissionQuery.data?.mainEvent}
                                    readonly={true}
                                />
                            </Disclosure.Panel>
                        </>
                    )}
                </Disclosure>
            )}
        </div>
    );
};
